import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { Genres } from 'enums';
import PaiLink from 'features/ui/base-components/pai-link';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import PaiButton from '../../ui/base-components/pai-button';
import PaiTextArea from '../../ui/base-components/pai-text-area';
import genresConfig, { TextGenerationCredentials } from '../genres-config';

interface Props {
  open: boolean;
  genre: Genres;
  textGenerationCredentials: TextGenerationCredentials;

  handleTextGenerationCredentialsChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleClose(): void;

  editProject(): void;
}

const EditTextGenerationProjectDialog: React.FC<Props> = ({
  open,
  genre,
  textGenerationCredentials,
  handleTextGenerationCredentialsChange,
  handleClose,
  editProject,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const hasEmptyField = useMemo(() => {
    for (const { genres, requiredFields } of genresConfig) {
      if (!_.isNil(genre) && genres.includes(genre)) {
        for (const field of requiredFields) {
          const value = _.get(textGenerationCredentials, field.valueKey);
          if (value === '' || value === undefined) {
            return true;
          }
        }
      }
    }
    return false;
  }, [genre, textGenerationCredentials]);

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullWidth={true} maxWidth={'xs'}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('editProject')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={2}>
          {genresConfig.map(({ genres, requiredFields }) =>
            !_.isNil(genre) && genres.includes(genre)
              ? requiredFields.map((field) => (
                  <PaiTextArea
                    key={field.valueKey}
                    value={_.get(textGenerationCredentials, field.valueKey) || ''}
                    name={field.nameKey}
                    onChange={handleTextGenerationCredentialsChange}
                    instruction={field.instruction}
                    placeholder={field.placeholder}
                    rows={field.rows}
                  />
                ))
              : null
          )}
          {genresConfig.map(({ genres, optionalFields }) =>
            !_.isNil(genre) && genres.includes(genre)
              ? optionalFields.map((field) => (
                  <PaiTextArea
                    key={field.valueKey}
                    value={_.get(textGenerationCredentials, field.valueKey) || ''}
                    name={field.nameKey}
                    optional={true}
                    onChange={handleTextGenerationCredentialsChange}
                    instruction={field.instruction}
                    placeholder={field.placeholder}
                    rows={field.rows}
                  />
                ))
              : null
          )}
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={handleClose} />
        <PaiButton text={t('ok')} action={editProject} disabled={hasEmptyField} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: 0,
  },
  title: {
    background: theme.palette.primary.main,
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default EditTextGenerationProjectDialog;
